<template>
  <div v-if="!iAmInWaitingRoom" class="meetingTimer" :class="{ open: timerBarOpen }" :rel="relMinutes">
    <div v-if="showBar" class="bar" :style="{ transform: `scaleX(${barWidth})` }" :class="timerClass" />
    <div v-if="showBar" class="labels" :class="timerClass">
      <div class="elapsed">
        {{ elapsedText }}
      </div>
      <div class="remaining">
        {{ remainingText }}
      </div>
    </div>
    <button :disabled="!showBar" @click="showTimer">Show meeting timer</button>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { splitDuration, asUnit } from '@/resources/time-helpers';
import { usePageStore } from '@/store/pinia/page';
import { usePhaseStore } from '@/store/pinia/phase';
import { useSidebarStore } from '@/store/pinia/sidebar';
import { useTimeStore } from '@/store/pinia/time';
import { useUsersStore } from '@/store/pinia/users';
import { useReady } from '@/composables/useReady';

import _ from 'underscore';
const pageStore = usePageStore();
const phaseStore = usePhaseStore();
const sidebarStore = useSidebarStore();
const timeStore = useTimeStore();
const usersStore = useUsersStore();

const { afterCard } = useReady();

const barWidth = ref(0);

const { current: currentSidebar } = storeToRefs(sidebarStore);
const { meetingLength, meetingElapsed, meetingOverdue, meetingOverdueBy, meetingRemaining, meetingEnd, now } = storeToRefs(timeStore);
const { isMeeting } = storeToRefs(pageStore);
const { currentPhase } = storeToRefs(phaseStore);
const { iAmInWaitingRoom } = storeToRefs(usersStore);

const timerBarOpen = computed(() => currentSidebar.value.name === 'meetingTime');
const longerThanHour = computed(() => meetingLength.value >= 1000 * 60 * 60);
const elapsedText = computed(() => {
  let { hours, mins, secs } = splitDuration(meetingElapsed.value);

  if (longerThanHour.value) {
    return `${hours}:${mins >= 10 ? mins : '0' + mins}:${secs >= 10 ? secs : '0' + secs}`;
  } else {
    return `${mins >= 10 ? mins : '0' + mins}:${secs >= 10 ? secs : '0' + secs}`;
  }
});

const remainingText = computed(() => {
  if (meetingOverdue.value) {
    let { hours, mins, secs } = splitDuration(meetingOverdueBy.value);

    if (longerThanHour.value) {
      return `-${hours}:${mins >= 10 ? mins : '0' + mins}:${secs >= 10 ? secs : '0' + secs}`;
    } else {
      return `-${mins >= 10 ? mins : '0' + mins}:${secs >= 10 ? secs : '0' + secs}`;
    }
  } else {
    let { hours, mins, secs } = splitDuration(meetingRemaining.value);
    if (longerThanHour.value) {
      return `${hours}:${mins >= 10 ? mins : '0' + mins}:${secs >= 10 ? secs : '0' + secs}`;
    } else {
      return `${mins >= 10 ? mins : '0' + mins}:${secs >= 10 ? secs : '0' + secs}`;
    }
  }
});

const amountThroughMeeting = computed(() => {
  if (meetingOverdue.value) {
    return 1;
  }
  var ratio = meetingElapsed.value / meetingLength.value;
  return ratio;
});

const outroLength = computed(() => {
  let l = asUnit(meetingLength.value, 'minutes');
  let out = 2;

  if (l > 120) {
    out = 20;
  } else if (l > 60) {
    out = 15;
  } else if (l > 30) {
    out = 10;
  } else if (l > 15) {
    out = 4;
  }

  return out;
});

const showBar = computed(() => {
  if (!isMeeting.value || iAmInWaitingRoom.value) {
    return false;
  }
  return currentPhase.value && !['waiting', 'init', 'lobby', 'endMeeting'].includes(currentPhase.value) && afterCard.value;
});

const timerClass = computed(() => {
  if (now.value > meetingEnd.value) {
    return 'overdue';
  } else if (asUnit(meetingRemaining.value, 'minutes') < outroLength.value) {
    return 'ending';
  } else {
    return 'running';
  }
});

const relMinutes = computed(() => Math.floor(asUnit(meetingLength.value, 'minutes')));

function showTimer() {
  if (iAmInWaitingRoom.value) return;
  sidebarStore.requestPane('meetingTime', { force: true });
}

function updateBarWidth(ratio) {
  barWidth.value = ratio;
}
const throttledUpdateBarWidth = _.throttle(updateBarWidth, 3000);
watch(amountThroughMeeting, throttledUpdateBarWidth);
</script>
<style lang="scss" scoped>
.meetingTimer {
  position: absolute;
  top: 0;
  right: 0;
  left: 0;
  height: rem($top-timer-expanded-height);
  background: transparent;
  z-index: 10;
  opacity: 0;
  transition: opacity 0.5s, margin-right $sidebar-duration $sidebar-ease-general;

  button {
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
    display: block;
    z-index: 10;
    opacity: 0;
    width: 100%;
  }

  .sidebar-opening &,
  .sidebar-open & {
    margin-right: rem($sidebar-width);
  }

  .isFullScreen & {
    margin-right: 0;
  }

  &::before {
    content: '';
    display: block;
    top: 0;
    position: absolute;
    left: 0;
    right: 0;
    height: rem($top-timer-mini-height);
    background: var(--c__bg);
    opacity: 0;
    transition: all 0.3s ease;
  }

  .meeting & {
    opacity: 1;
  }

  .lobby &,
  .endMeeting &,
  .endMeeting-animateIn & {
    opacity: 0;
  }

  .bar {
    position: absolute;
    left: 0;
    height: rem($top-timer-mini-height);
    top: 0;
    background: var(--c__time-ok);
    width: 100%;
    transform-origin: left;
    transform: scaleX(0);
    transition: height 0.3s ease, background-color 0.3s, transform 3s linear;
  }

  .bar.ending {
    background: var(--c__time-mid);
  }
  .bar.overdue {
    background: var(--c__time-over-light);
  }

  .labels {
    opacity: 0;
    transition: all 0.3s;
    color: var(--c__text-loud);
    & > * {
      position: absolute;
      top: 0;
      line-height: rem($top-timer-expanded-height);
    }
  }

  .labels.overdue {
    color: var(--c__bg);
  }

  .elapsed {
    left: 0.5em;
  }

  .remaining {
    right: 0.5em;
  }

  &:hover,
  &.open {
    .labels {
      opacity: 1;
    }
    &::before {
      opacity: 1;
      height: rem($top-timer-expanded-height);
    }
    .bar {
      height: rem($top-timer-expanded-height);
    }
  }
}
</style>
